import { Directive, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';

import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  endWith,
  interval,
  map,
  switchMap,
  takeUntil,
  takeWhile
} from 'rxjs';

import { DisplayType, SummitDisplay, SummitDisposing, ViewField } from '@summit/models';

//https://dev.to/angular/building-count-up-animation-with-angular-and-rxjs-240k

const easeOutQuad = (x: number): number => x * (2 - x);

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[countUp]',
  standalone: true
})
export class CountUpDirective extends SummitDisposing implements OnInit {
  @Input('countUp')
  set count(count: number) {
    // emit a new value to the `count$` subject
    //if (count == -1621.23) console.log(`jeff -counting up to ${count}`);
    this.count$.next(count);
  }

  @Input()
  set duration(duration: number) {
    // emit a new value to the `duration$` subject
    this.duration$.next(duration);
  }
  @Input() displayType: DisplayType;

  // default count value is 0
  private readonly count$ = new BehaviorSubject(0);
  // default duration value is 2000 ms
  private readonly duration$ = new BehaviorSubject(2000);

  private readonly currentCount$ = combineLatest([this.count$, this.duration$]).pipe(
    switchMap(([count, duration]) => {
      // get the time when animation is triggered
      const startTime = animationFrameScheduler.now();

      // use `animationFrameScheduler` for better rendering performance
      return interval(10, animationFrameScheduler).pipe(
        // calculate elapsed time
        map(() => animationFrameScheduler.now() - startTime),
        // calculate progress
        map((elapsedTime) => elapsedTime / duration),
        // complete when progress is 1
        takeWhile((progress) => progress < 1, true),
        // apply quadratic ease-out function
        // for faster start and slower end of counting
        map(easeOutQuad),
        // calculate current count with catch
        map((progress) => (progress >= 1 ? count : Math.round(progress * count))),
        //Don't flash a higher value
        map((countValue) => (Math.abs(countValue) >= Math.abs(count) ? count : countValue)),
        // make sure that last emitted value is count
        endWith(count), //doesn't trigger, but left in
        distinctUntilChanged()
      );
    })
  );

  constructor(private readonly elementRef: ElementRef, private readonly renderer: Renderer2) {
    super();
  }

  ngOnInit(): void {
    this.displayCurrentCount();
  }

  private displayCurrentCount(): void {
    this.currentCount$.pipe(takeUntil(this.disposing$)).subscribe((currentCount) => {
      if (this.displayType)
        this.renderer.setProperty(
          this.elementRef.nativeElement,
          'innerHTML',
          SummitDisplay.formatAutoTextLabel(currentCount, { displayType: this.displayType } as ViewField<unknown>)
        );
      else this.renderer.setProperty(this.elementRef.nativeElement, 'innerHTML', currentCount);
    });
  }
}
